import { Injectable, inject } from '@angular/core';
import { ApiService, GenericResponseCode } from '@brighthub/api';
import { Observable, forkJoin, map } from 'rxjs';
import {
  CarePlanOptionTitle,
  OptionReferenceCapabilities,
  OptionReferenceData,
  OptionReferenceValue,
  buildOptionStatusTransitions,
} from '../options.model';
import {
  SystemCodeCatalog,
  SystemCodeCatalogService,
} from '../../system-codes/system-code-catalog.service';
import { OptionReferenceEnvelope } from './option-reference-api.model';
import type { OptionReferenceApi } from './option-reference-api.token';

const API_NAME = 'api-internal';
const OPTION_TYPE_CODE_TYPE = 'option_type';
const OPTION_STATUS_CODE_TYPE = 'option_status';

/** Live Matrix and System Codes adapter for the Care Plan Options UI. */
@Injectable()
export class HttpOptionReferenceService implements OptionReferenceApi {
  private readonly _api = inject(ApiService);
  private readonly _systemCodes = inject(SystemCodeCatalogService);

  readonly capabilities: OptionReferenceCapabilities = {
    allowCustomTitle: true,
    canRemove: false,
    canUpdateType: false,
  };

  loadReferenceData(clientId: number | null): Observable<OptionReferenceData> {
    const resolvedClientId = this._clientId(clientId);

    return forkJoin({
      matrix: this._api.get<OptionReferenceEnvelope<unknown>>(
        API_NAME,
        `/option-matrix?clientId=${resolvedClientId}`,
      ),
      codes: this._systemCodes.load(),
    }).pipe(
      map(({ matrix, codes }) => {
        const optionTypes = this._referenceValues(codes, OPTION_TYPE_CODE_TYPE);
        const optionStatuses = this._referenceValues(
          codes,
          OPTION_STATUS_CODE_TYPE,
        );
        return {
          optionTypes,
          optionStatuses,
          optionTitles: this._titles(
            this._matrixEntries(this._payload(matrix)),
            optionTypes,
          ),
          allowedTransitions: buildOptionStatusTransitions(optionStatuses),
        };
      }),
    );
  }

  private _clientId(value: number | null): number {
    if (!Number.isSafeInteger(value) || Number(value) <= 0) {
      throw new Error(
        'A valid client id is required to load option references.',
      );
    }
    return Number(value);
  }

  private _payload<T>(response: OptionReferenceEnvelope<T>): T {
    if (
      response?.result?.code !== GenericResponseCode.SERVICE_OK ||
      response.payload == null
    ) {
      throw new Error(
        response?.result?.message ||
          'Option reference API returned an invalid response',
      );
    }
    return response.payload;
  }

  private _referenceValues(
    catalog: SystemCodeCatalog,
    codeType: string,
  ): OptionReferenceValue[] {
    return this._systemCodes
      .values(catalog, codeType)
      .map(({ code, label }) => ({ code, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  private _titles(
    resources: readonly unknown[],
    optionTypes: readonly OptionReferenceValue[],
  ): CarePlanOptionTitle[] {
    const titles = new Map<string, CarePlanOptionTitle>();
    for (const resource of resources ?? []) {
      const title = this._string(resource, 'optionTitle', 'title', 'name');
      const suppliedOptionType = this._string(
        resource,
        'optionType',
        'optionTypeCode',
      );
      const optionTypeCode = suppliedOptionType
        ? this._optionTypeCode(suppliedOptionType, optionTypes)
        : null;
      if (!title || (suppliedOptionType && !optionTypeCode)) continue;
      const id = `${optionTypeCode ?? 'UNTYPED'}:${title.toLowerCase()}`;
      if (titles.has(id)) continue;
      titles.set(id, {
        id,
        title,
        optionTypeCode,
        requiresQualification:
          this._boolean(resource, 'requiresQualification') !== false,
        qualificationPrompt: this._string(
          resource,
          'qualificationPrompt',
          'prompt',
        ),
      });
    }
    return [...titles.values()];
  }

  private _optionTypeCode(
    value: string | null,
    optionTypes: readonly OptionReferenceValue[],
  ): string | null {
    if (!value) return null;
    const comparableValue = this._comparable(value);
    return (
      optionTypes.find(
        (optionType) =>
          this._comparable(optionType.code) === comparableValue ||
          this._comparable(optionType.label) === comparableValue,
      )?.code ?? null
    );
  }

  private _comparable(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private _matrixEntries(payload: unknown): readonly unknown[] {
    const record = this._record(payload);
    const entries =
      record?.['optionMatrixEntries'] ??
      record?.['options'] ??
      record?.['items'] ??
      payload;
    if (!Array.isArray(entries)) {
      throw new Error(
        'Option reference API returned an invalid option matrix.',
      );
    }
    return entries;
  }

  private _record(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  }

  private _string(value: unknown, ...keys: string[]): string | null {
    const record = this._record(value);
    for (const key of keys) {
      const candidate = record?.[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return null;
  }

  private _boolean(value: unknown, ...keys: string[]): boolean | null {
    const record = this._record(value);
    for (const key of keys) {
      const candidate = record?.[key];
      if (typeof candidate === 'boolean') return candidate;
    }
    return null;
  }
}
